import React, { useMemo, useState } from 'react';
import { DEFAULT_COMPOSED_CHART_X_HEIGHT } from '@components/Widgets/utils';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, emptyArray, emptyObject } from '@fabric-ui/utils';
import { Box, debounce, useTheme } from '@mui/material';
import { BarChart, CartesianGrid, ComposedChart, LabelList, LabelListProps, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart';
import { isPercentageMetric } from '../../../utils/dashboardDataUtils';
import { normalize100PercentData } from '../../../utils/utils';
import AvLegend from '../../AvLegend';
import { componentType } from '../constants';
import { BarLineLabel } from '../CustomChartLabel';
import CustomTooltip from '../CustomTooltip';
import type { AvComposedWidgetProps } from './AvComposedWidget.types';
import {
  formatValue,
  getChartMargin,
  getLabelPosition,
  getMinXTickSize,
  getMinYTickSize,
  getXAngle,
  getXTickHeight,
  getXTickMargin,
  getYAngle,
  getYAxisWidth,
  TICK_COUNT,
} from './AvComposedWidget.utils';
import AvComposedWidgetCustomTick from './AvComposedWidgetCustomTick';
import { useAvComposedWidgetInterval } from './useAvComposedWidgetInterval';

function AvComposedWidget({
  data: d = emptyArray,
  series = emptyArray,
  isDate,
  xAxisKey,
  showLegend = true,
  labels = emptyObject,
  additionalAxis,
  chartProps,
  defaultAxisProps: _defaultAxisProps,
  tooltipProps = { showContentLegend: true, metricsToHide: emptyArray, showTitleLegend: false },
  dateFormat,
  getTooltipContent,
  sx = emptyObject,
  className,
  selected = emptyArray,
  onSelect,
  tooltipComponentProps: { shared, ...tooltipComponentProps } = {},
  clickInteractions,
  responsiveContainerProps = emptyObject,
  showCartesianGridProps = emptyObject,
  getFormattedColorCell,
  hasFormattingRules,
  isStacked,
  showDataLabels,
  enableDefaultMinPointSize,
  enableLockTooltip,
}: AvComposedWidgetProps) {
  const { x, ...defaultAxisProps } = _defaultAxisProps || {};
  const { height: xHeight, ...restX } = x || {};

  const activeClickedCell = React.useRef<Element | null>(null);
  const [temporaryClickedCell, setTemporaryClickedCell] = useState<{ d: any; dataKey: any }>();

  const series100Percent = series.filter(({ is100Percent }) => is100Percent);
  const is100Percent = series100Percent.length;
  const data = useMemo(() => (is100Percent ? normalize100PercentData(d, series) : d), [d, series]);

  const theme = useTheme();
  const [hoveredCell, setHoveredCell] = useState<{ name?: string | number; dataKey?: string | number; d: unknown }>();
  const rightAxisMetrics = useMemo(() => series.filter(s => isPercentageMetric(s.dataKey)), [series]);
  const isBar = series.every(({ type }) => type === 'bar');
  const isHorizontal = chartProps?.layout !== 'vertical';
  const shouldRotateTicks = data.length > 6 || (!is100Percent && chartProps?.shouldRotateTicks);
  const [lineTooltipLock, setLineTooltipLock] = React.useState(false);

  const xTickRotation = getXAngle(chartProps?.xAxisTickType, shouldRotateTicks);
  const yTickRotation = getYAngle(chartProps?.yAxisTickType);

  const { intervalX, intervalY, responsiveContainerRef, handleResize } = useAvComposedWidgetInterval({
    minXTickSize: getMinXTickSize(xTickRotation),
    minYTickSize: getMinYTickSize(yTickRotation),
    totalXTicks: isHorizontal ? data.length : TICK_COUNT,
    totalYTicks: !isHorizontal ? data.length : TICK_COUNT,
  });

  const handleInteractionClick = cell => {
    const available = clickInteractions?.filter(v => v.isEnabledForCell(cell));
    if (available?.length === 1) {
      return available[0].onClick(cell);
    }
    if (available?.length) {
      return setTemporaryClickedCell(cell);
    }
    return undefined;
  };

  const onCellSelection = cell => {
    if (!clickInteractions || selected.length) {
      return onSelect?.(cell);
    }
    return handleInteractionClick(cell);
  };

  const handleLineChartClick = nextState => {
    if (enableLockTooltip) {
      setLineTooltipLock(p => !p);
    } else if (Object.values(nextState).length) {
      onCellSelection({
        dataKey: nextState?.activePayload?.[0].dataKey,
        d: nextState?.activePayload?.[0].payload,
        widgetData: data,
      });
    }
  };

  const handleBarCharClick: CategoricalChartFunc = (next, event) => {
    if (shared && !temporaryClickedCell) {
      handleInteractionClick(next);
      event?.stopPropagation();
    }
  };

  const xAxisHeight =
    xHeight || (isHorizontal ? getXTickHeight(xTickRotation, !!labels.x, !!x?.customTickFormatter) : DEFAULT_COMPOSED_CHART_X_HEIGHT);

  const tickFormatterX = value => formatValue(isDate, dateFormat, value);
  const tickFormatterY = tick => `${abbreviateNumber(tick)}${is100Percent ? '%' : ''}`;
  const handleHover = React.useRef(debounce(setHoveredCell, 100));

  const tooltipActive = isBar
    ? shared
      ? Boolean(hoveredCell || temporaryClickedCell) || undefined
      : Boolean(hoveredCell || temporaryClickedCell)
    : undefined;

  const tooltipTrigger = (() => {
    const currentCell = isBar ? temporaryClickedCell : lineTooltipLock;
    return currentCell ? 'click' : 'hover';
  })();

  const content = (
    <>
      <CartesianGrid
        stroke="var(--color-border-base-primary)"
        vertical={!isHorizontal}
        horizontal={isHorizontal}
        {...showCartesianGridProps}
      />
      <XAxis
        label={{
          value: labels.x,
          position: 'insideBottom',
          style: { fill: 'var(--color-content-base-secondary)', fontSize: '12px', fontWeight: 400, fontFamily: 'Inter', height: '18px' },
        }}
        dataKey={isHorizontal ? xAxisKey : undefined}
        type={isHorizontal ? 'category' : 'number'}
        axisLine={false}
        tickLine={false}
        tickCount={TICK_COUNT}
        tickMargin={isHorizontal ? getXTickMargin(xTickRotation, !!x?.customTickFormatter) : 0}
        interval={intervalX}
        height={xAxisHeight}
        tick={
          <AvComposedWidgetCustomTick
            axis="X"
            rotation={xTickRotation}
            customTickFormatter={isHorizontal ? x?.customTickFormatter : undefined}
          />
        }
        tickFormatter={isHorizontal ? tickFormatterX : tickFormatterY}
        {...restX}
      />
      <YAxis
        label={{
          value: labels.y,
          angle: -90,
          position: 'insideLeft',
          style: {
            fill: 'var(--color-content-base-secondary)',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Inter',
            textAnchor: 'middle',
          },
        }}
        tickMargin={4}
        yAxisId="left"
        tickCount={TICK_COUNT}
        width={getYAxisWidth({
          hasLabel: !!labels.y,
          isVertical: !isHorizontal,
          customTickFormatter: !!x?.customTickFormatter,
          customWidth: labels.yAxisWidth,
        })}
        interval={intervalY}
        dataKey={isHorizontal ? undefined : xAxisKey}
        type={isHorizontal ? 'number' : 'category'}
        orientation="left"
        axisLine={false}
        tickLine={false}
        allowDecimals={false}
        tickFormatter={isHorizontal ? tickFormatterY : tickFormatterX}
        tick={
          <AvComposedWidgetCustomTick
            axis="Y"
            rotation={yTickRotation}
            customTickFormatter={!isHorizontal ? x?.customTickFormatter : undefined}
          />
        }
        {...defaultAxisProps?.y}
      />
      {additionalAxis}
      {!!rightAxisMetrics?.length && (
        <YAxis
          yAxisId="right"
          tickCount={TICK_COUNT}
          tickMargin={15}
          orientation="right"
          axisLine={false}
          tickLine={false}
          tickFormatter={tick => `${tick}%`}
          domain={[0, 1]}
          allowDecimals={false}
          {...defaultAxisProps?.y}
        />
      )}
      <Tooltip
        cursor={{ fill: 'rgb(from var(--color-surface-interactive-secondary-default) r g b / 0.35)' }}
        active={tooltipActive}
        trigger={tooltipTrigger}
        // @ts-expect-error: Safe - props passed to content but not typed
        onMouseEnter={(e: Event) => {
          e.stopPropagation();
          handleHover.current.clear();
        }}
        onMouseLeave={(e: Event) => {
          e.stopPropagation();
          handleHover.current(undefined);
        }}
        onClick={(e: Event) => {
          e.stopPropagation();
          if (hoveredCell?.dataKey) {
            setTemporaryClickedCell({ d: hoveredCell.d, dataKey: hoveredCell.dataKey });
          }
        }}
        activeCellDataKey={hoveredCell?.dataKey || temporaryClickedCell?.dataKey}
        content={
          getTooltipContent ||
          /* eslint-disable-next-line react/no-unstable-nested-components */
          (({ payload } = {}) => (
            <CustomTooltip
              payload={payload}
              isDate={isDate}
              {...tooltipProps}
              dateFormat={dateFormat}
              series100Percent={series100Percent}
              clickInteractions={clickInteractions}
              areClickOptionsVisible={!!temporaryClickedCell}
              setAreClickOptionsVisible={() => setTemporaryClickedCell(undefined)}
              onInteractionsClick={() => onCellSelection({ dataKey: payload?.[0].dataKey, d: payload?.[0] })}
            />
          ))
        }
        wrapperStyle={{ zIndex: theme.zIndex.modal, pointerEvents: 'auto', maxHeight: '300px', cursor: 'auto' }}
        {...tooltipComponentProps}
        shared={isBar && shared ? !(hoveredCell || temporaryClickedCell) : shared}
      />
      {series.map((s, index) =>
        componentType[s.type]({
          props: s,
          theme,
          selected,
          onSelect: (cell, event) => {
            const isClickedCell = activeClickedCell.current === event.target;
            if (isClickedCell) {
              activeClickedCell.current = null;
              setTemporaryClickedCell(undefined);
            } else {
              activeClickedCell.current = event.target;
              onCellSelection(cell);
            }
          },
          data,
          hoveredCell: temporaryClickedCell ? { ...temporaryClickedCell.d, dataKey: temporaryClickedCell.dataKey } : hoveredCell,
          getFormattedColorCell,
          index,
          series,
          setHoveredCell: v => handleHover.current(v),
          renderLabelList: showDataLabels
            ? (props?: LabelListProps<typeof data>) => (
                // @ts-expect-error: applying custom "position" prop
                <LabelList
                  content={BarLineLabel}
                  dataKey={s.dataKey}
                  {...(isBar ? getLabelPosition(isHorizontal ? 'horizontal' : 'vertical', !!isStacked) : { position: 'top' })}
                  {...props}
                />
              )
            : undefined,
          enableDefaultMinPointSize,
        })
      )}
    </>
  );

  return (
    <Box
      sx={{
        ...flex.col,
        height: '100%',
        gap: 1,
        ...(Array.isArray(sx) ? sx : [sx]),
        svg: { pointerEvents: 'auto' },
        display: showLegend ? 'grid' : 'flex',
        gridTemplateRows: showLegend ? '24px 1fr' : undefined,
        '&:has(.hidden-legends)': {
          gridTemplateRows: showLegend ? '42px 1fr' : undefined,
        },
      }}
      onMouseLeave={() => setTemporaryClickedCell(undefined)}
      onClick={({ target }) => {
        if (target instanceof Element && target.classList.contains('recharts-surface')) {
          setTemporaryClickedCell(undefined);
        }
      }}>
      {showLegend && (
        <Box overflow="hidden" maxHeight="42px">
          <AvLegend series={series} isHorizontal getMaxLegendsToShow={calculated => calculated - 1} />
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: 'hidden', ...responsiveContainerProps.sx }}>
        <ResponsiveContainer
          onResize={handleResize}
          debounce={100}
          // @ts-expect-error: Recharts is weird
          ref={responsiveContainerRef}
          className={className}
          width={responsiveContainerProps.width ?? '100%'}
          height={responsiveContainerProps.height ?? '100%'}>
          {isBar ? (
            <BarChart
              {...chartProps}
              data={data}
              onClick={handleBarCharClick}
              style={isStacked && hasFormattingRules && { stroke: theme.palette.white.main, strokeWidth: 1 }}
              margin={getChartMargin(!!showDataLabels, isHorizontal, isBar)}
              className={chartProps?.layout || 'horizontal'}>
              {content}
            </BarChart>
          ) : (
            <ComposedChart
              margin={getChartMargin(!!showDataLabels, isHorizontal, isBar)}
              {...chartProps}
              data={data}
              onClick={handleLineChartClick}
              style={{ cursor: 'pointer' }}>
              {content}
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default AvComposedWidget;
